import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import MedicalDetails from "./MedicalDetails";
import OrderDetailsOpportunity from "./OrderDetailsOpportunity";
import PhysicianOpportunity from "./PhysicianOpportunity";

const OrderDetailsForm = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const [validation, setValidation] = useState({
    medical: false,
    order: true, // Initially true based on default values
    physician: true, // Initially true based on default values
  });

  const setMedicalValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.medical === isValid) return prev;
      return { ...prev, medical: isValid };
    });
  }, []);

  const setOrderValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.order === isValid) return prev;
      return { ...prev, order: isValid };
    });
  }, []);

  const setPhysicianValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.physician === isValid) return prev;
      return { ...prev, physician: isValid };
    });
  }, []);

  useEffect(() => {
    if (onValidationChange) {
      const allValid = Object.values(validation).every((v) => v === true);
      onValidationChange(allValid);
    }
  }, [validation, onValidationChange]);

  return (
    <Box sx={{ display: "flex", gap: 3, width: "100%", alignItems: "stretch" }}>
      {/* Left Column: Medical Details */}
      <Box
        sx={{
          flex: 1.2,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MedicalDetails
          opportunity={opportunity}
          data={data.medical}
          onDataChange={useCallback(
            (newData) => onDataChange("medical", newData),
            [onDataChange],
          )}
          onValidationChange={setMedicalValid}
          showErrors={showErrors}
        />
      </Box>

      {/* Right Column: Order and Physician Stack */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <OrderDetailsOpportunity
          opportunity={opportunity}
          data={data.opportunity}
          onDataChange={useCallback(
            (newData) => onDataChange("opportunity", newData),
            [onDataChange],
          )}
          onValidationChange={setOrderValid}
          showErrors={showErrors}
        />
        <PhysicianOpportunity
          opportunity={opportunity}
          data={data.physician}
          onDataChange={useCallback(
            (newData) => onDataChange("physician", newData),
            [onDataChange],
          )}
          onValidationChange={setPhysicianValid}
          showErrors={showErrors}
        />
      </Box>
    </Box>
  );
};

OrderDetailsForm.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default OrderDetailsForm;
